import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { validateCheckout, validateCheckoutForm } from './checkoutValidation'
import { isAllDigitalDelivery } from './cartValidation'

/**
 * Determines payment gateway type from portal config
 * @param {Object} portal - Portal configuration
 * @returns {string} - 'stripe' | 'authorize_net'
 */
const getPaymentGatewayType = portal => {
  const gatewayType = portal?.payment_gateway?.type
  switch (gatewayType) {
    case 'stripe':
      return 'stripe'
    case 'authorize_net':
      return 'authorize_net'
    default:
      return 'authorize_net'
  }
}

/**
 * Hook that provides checkout validation state
 * Replaces the complex validation useEffect in CheckoutOrderSummary
 *
 * @param {Object} options
 * @param {boolean} options.checkoutFormIsValid - External form validation state
 * @param {boolean} options.checkoutHasNoErrors - External error state
 * @returns {{ canProceed: boolean, validationState: Object, allDigital: boolean }}
 */
export const useCheckoutValidation = ({ checkoutFormIsValid = true, checkoutHasNoErrors = true } = {}) => {
  const checkout = useSelector(state => state.checkout)
  const cartItems = useSelector(state => state.cartItems)
  const customerGroup = useSelector(state => state.customerGroup)
  const reduxErrors = useSelector(state => state.errors)
  const portal = useSelector(state => state.portal)
  const youPay = useSelector(state => state.checkout.youPay)
  const checkoutErrors = useSelector(state => state.checkout.errors)
  const checkoutFormData = useSelector(state => state.checkout.checkoutFormData)
  const requireBillingAddressForFreeOrders = useSelector(
    state => state.storeViews?.current?.require_billing_address_for_free_orders
  )

  const allDigital = useMemo(() => isAllDigitalDelivery(cartItems), [cartItems])
  const paymentGatewayType = useMemo(() => getPaymentGatewayType(portal), [portal])

  const formIsValid = useMemo(() => {
    if (!checkoutFormData || checkoutFormData.length === 0) return true
    return validateCheckoutForm(checkoutFormData)
  }, [checkoutFormData])

  const { canProceed, validationState } = useMemo(() => {
    const result = validateCheckout({
      checkout,
      customerGroup,
      errors: checkoutErrors,
      reduxErrors,
      youPay,
      paymentGatewayType,
      allDigital,
      checkoutFormIsValid: checkoutFormIsValid && formIsValid,
      checkoutHasNoErrors,
      requireBillingAddressForFreeOrders
    })

    return result
  }, [
    checkout,
    customerGroup,
    checkoutErrors,
    reduxErrors,
    youPay,
    paymentGatewayType,
    allDigital,
    checkoutFormIsValid,
    formIsValid,
    checkoutHasNoErrors,
    requireBillingAddressForFreeOrders
  ])

  return {
    canProceed,
    validationState,
    allDigital,
    paymentGatewayType
  }
}

export default useCheckoutValidation

